import { Box, TableCell, TableRow } from "@mui/material";
import DotLegalPaper from "../../common/components/dotLegalPaper/DotLegalPaper";
import { DotLegalButton, DotLegalEmptyState, DotLegalOverflowMenu, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";
import { useTranslation } from "../../localization/useTranslation";
import { useLegalEntityDocumentTabDataMapping } from "./LegalEntityDocumentTab.hooks";
import NoSharings from "../../processingActivity/processingActivityOverview/processingActivitySharingsTab/noSharings.svg?react";
import { LegalEntityDocumentRow } from "./LegalEntityDocumentTab.types";
import AddLegalEntityDocumentDialog from "./AddLegalEntityDocumentDialog/AddLegalEntityDocumentDialog";
import { DeleteLegalEntityDocumentDialog } from "./deleteLegalEntityDocumentDialog/DeleteLegalEntityDocumentDialog";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import React from "react";
import EditLegalEntityDocumentDialog from "./editLegalEntityDocumentDIalog/EditLegalEntityDocumentDialog";
import { DocumentStatus } from "../../documents/Documents.types";
import { DocumentTypeUsageType } from "../../masterData/documentType/documentTypes/DocumentTypes.types";
import DocumentPreviewLink from "../../common/components/documentPreview/DocumentPreviewLink";
import { useDocumentPreviewManager } from "../../common/components/documentPreview/useDocumentPreviewManager";
import { EntityType } from "../../common/Common.types";
import { isNullOrWhitespace } from "../../common/stringOperations.ts";
import AuditLogDialog from "../../auditlogDialog/AuditLogDialog.tsx";
import BestPracticeCoverageResult from "../../common/components/bestPracticeCheckResultDialog/BestPracticeCoverageResult.tsx";
import BestPracticeCheckDialog from "../../common/components/bestPracticeCheckDialog/BestPracticeCheckDialog.tsx";
import BestPracticeCheckResultDialog from "../../common/components/bestPracticeCheckResultDialog/BestPracticeCheckResultDialog.tsx";
import TableHeaderLock from "../../common/components/TableHeaderLock";
import { DownloadAllDocumentsButton } from "@common/components/downloadAllDocuments/DownloadAllDocumentsButton";
import { useAiFeatureAccess } from "../../plan/aiFeatureGate/useAiFeatureAccess";

export interface ILegalEntityDocumentTabProps {
    legalEntityId: string | undefined;
    legalEntityName?: string;
    isGroupEntity: boolean;
    onDocumentsChanged: () => void;
}

function LegalEntityDocumentTab(props: ILegalEntityDocumentTabProps) {
    const { translateString, translateDate } = useTranslation();
    const { permissions } = useUserContext();
    const { showAiQualityAssurance, planActivated } = useAiFeatureAccess();

    const {
        hasDocuments,
        isLoading,
        data,
        documents,
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        hasActiveFilters,
        getMenuItems,
        showAddEditDialog,
        setShowAddEditDialog,
        selectedDocument,
        setSelectedDocument,
        refetchDocuments,
        showDeleteDocumentDialog,
        setShowDeleteDocumentDialog,
        showEditDialog,
        setShowEditDialog,
        showAuditLogDialog,
        setShowAuditLogDialog,
        bestPracticeCheckDocument,
        setBestPracticeCheckDocument,
        bestPracticeCheckDocumentToQualityAssurance,
        setBestPracticeCheckDocumentToQualityAssurance,
        bestPracticeCheckInitialResult,
        setBestPracticeCheckInitialResult,
        onCheckComplete,
    } = useLegalEntityDocumentTabDataMapping(props);

    const { previewableDocuments, handleDocumentClick, getPreviewHref } = useDocumentPreviewManager({
        documents: data ?? [],
        entityType: EntityType.LegalEntityDocument,
        entityId: props.legalEntityId!,
        entityTitle: props.legalEntityName,
    });

    function formatGroupEntities(row: LegalEntityDocumentRow) {
        if (row.allGroupEntities) {
            return translateString("entireGroup");
        }

        return row.groupEntities.join(", ");
    }

    return (
        <DotLegalPaper>
            {hasDocuments ? (
                <DotLegalTableWithFilterBar
                    filterBar={{
                        isLoading: isLoading,
                        filterData: getFilterOptions(),
                        fetchFilterData: true,
                        onMenuClose: onFilterChange,
                        onRemove: onFilterChange,
                        pinnedFilterIds: pinnedFilterIds,
                        onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                    }}
                    headers={GetDocumentHeader(translateString, props.isGroupEntity, showAiQualityAssurance, planActivated)}
                    rowsPerPage={30}
                    defaultOrderBy={"name"}
                    defaultOrder={"asc"}
                    isLoading={isLoading}
                    data={documents}
                    clickableRows
                    hasOverflowMenu
                    emptyText={translateString(hasActiveFilters ? "noDataMatchingCriteria" : "noDocumentsAttachedToLegalEntity")}
                    renderRow={(row, i) => (
                        <TableRow key={i} onClick={() => handleDocumentClick(row)}>
                            <TableCell>
                                <DocumentPreviewLink previewHref={getPreviewHref(row)} onPreviewClick={() => handleDocumentClick(row)}>
                                    {row.name}
                                </DocumentPreviewLink>
                            </TableCell>
                            <TableCell>{row.fileName ?? row.link}</TableCell>
                            <TableCell>{row.documentType.name}</TableCell>
                            <TableCell>{translateString(DocumentStatus[row.status].toLowerCase())}</TableCell>
                            <TableCell>{row.owner.name}</TableCell>
                            <TableCell>{row.version}</TableCell>
                            {!props.isGroupEntity && <TableCell>{formatGroupEntities(row)}</TableCell>}
                            <TableCell>{translateDate(row.created)}</TableCell>
                            {showAiQualityAssurance && (
                                <TableCell>
                                    {!isNullOrWhitespace(row.fileName) && (
                                        <BestPracticeCoverageResult
                                            result={row.aiQualityAssuranceResult}
                                            isTooLargeForAi={row.isTooLargeForAi}
                                            onClick={() => {
                                                if (row.aiQualityAssuranceResult) {
                                                    setBestPracticeCheckDocument({
                                                        documentId: row.documentId,
                                                    });
                                                } else {
                                                    setBestPracticeCheckDocumentToQualityAssurance({
                                                        documentId: row.documentId,
                                                    });
                                                }
                                            }}
                                        />
                                    )}
                                </TableCell>
                            )}
                            <TableCell align={"right"}>
                                <DotLegalOverflowMenu items={getMenuItems(row)} />
                            </TableCell>
                        </TableRow>
                    )}
                />
            ) : (
                <DotLegalEmptyState icon={<NoSharings />} text={translateString("noDocumentsAttachedToLegalEntity")} />
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2, gap: 2 }}>
                <DownloadAllDocumentsButton
                    entityType={EntityType.LegalEntity}
                    entityId={props.legalEntityId!}
                    show={documents.length > 1 || documents.some((x) => x.version > 1)}
                />
                {permissions.companyDocumentationPermissions.create && (
                    <DotLegalButton
                        buttonType={"primary"}
                        onClick={() => {
                            setSelectedDocument(undefined);
                            setShowAddEditDialog(true);
                        }}
                    >
                        {translateString("createNew")}
                    </DotLegalButton>
                )}
            </Box>

            {showAddEditDialog && (
                <AddLegalEntityDocumentDialog
                    onCloseDialog={() => {
                        props.onDocumentsChanged();
                        setShowAddEditDialog(false);
                        refetchDocuments();
                    }}
                    isGroupEntity={props.isGroupEntity}
                />
            )}

            {showDeleteDocumentDialog && (
                <DeleteLegalEntityDocumentDialog
                    selectedDocument={selectedDocument!}
                    onCloseDialog={() => {
                        setShowDeleteDocumentDialog(false);
                    }}
                    onSaveCorrect={() => {
                        props.onDocumentsChanged();
                        setShowDeleteDocumentDialog(false);
                        refetchDocuments();
                    }}
                    legalEntityId={props.legalEntityId}
                />
            )}

            {showEditDialog && (
                <EditLegalEntityDocumentDialog
                    onCloseDialog={() => {
                        refetchDocuments();
                        setShowEditDialog(false);
                    }}
                    documentId={selectedDocument!.documentId}
                    legalEntityId={selectedDocument!.legalEntityId!}
                    legalEntityName={props.legalEntityName}
                    isGroupEntity={props.isGroupEntity}
                    isExtended
                    documentTypeUsageType={DocumentTypeUsageType.AllTransfers}
                />
            )}

            {!isNullOrWhitespace(showAuditLogDialog) && (
                <AuditLogDialog
                    entityType={EntityType.LegalEntityDocument}
                    entityId={showAuditLogDialog!}
                    entityName={documents.find((x) => x.legalEntityDocumentId === showAuditLogDialog)?.name ?? ""}
                    onDialogClose={() => setShowAuditLogDialog(null)}
                />
            )}

            {bestPracticeCheckDocumentToQualityAssurance && (
                <BestPracticeCheckDialog
                    documentId={bestPracticeCheckDocumentToQualityAssurance.documentId}
                    onDialogClose={() => {
                        setBestPracticeCheckDocumentToQualityAssurance(undefined);
                        refetchDocuments();
                    }}
                    onCheckComplete={onCheckComplete}
                />
            )}

            {bestPracticeCheckDocument && (
                <BestPracticeCheckResultDialog
                    documentId={bestPracticeCheckDocument.documentId}
                    initialResult={bestPracticeCheckInitialResult}
                    onDialogClose={() => {
                        setBestPracticeCheckDocument(undefined);
                        setBestPracticeCheckInitialResult(undefined);
                        refetchDocuments();
                    }}
                />
            )}
        </DotLegalPaper>
    );
}

export default LegalEntityDocumentTab;

function GetDocumentHeader(translateString: any, isGroupEntity: boolean, showAiQualityAssurance: boolean, planActivated: boolean) {
    const headers = Array<ITableHeader<LegalEntityDocumentRow>>();
    headers.push({ property: "name", text: translateString("name"), align: "left", showOnMobile: true });
    headers.push({ property: "fileName", text: translateString("document"), align: "left", showOnMobile: true });
    headers.push({ property: "documentType", text: translateString("type"), align: "left", showOnMobile: true });
    headers.push({ property: "status", text: translateString("status"), align: "left", showOnMobile: true });
    headers.push({ property: "owner", text: translateString("owner"), align: "left", showOnMobile: true });
    headers.push({ property: "version", text: translateString("documentVersion"), align: "left", showOnMobile: true });

    if (!isGroupEntity) {
        headers.push({ property: "groupEntities", text: translateString("groupEntities"), align: "left", showOnMobile: true });
    }

    headers.push({ property: "created", text: translateString("created"), align: "left", showOnMobile: true });

    if (showAiQualityAssurance) {
        headers.push({
            property: "aiQualityAssuranceResult",
            text: translateString("aiQualityScan"),
            extraHeaderContent: !planActivated ? <TableHeaderLock tooltip={translateString("aiAddOnPurchaseTooltip")} /> : undefined,
        });
    }

    return headers;
}
